import React from "react";
import { useTranslation } from "react-i18next";

interface DualColorProgressBarProps {
  subscriptionRemaining: number;
  subscriptionMax: number;
  oneTimeRemaining: number;
  oneTimeMax: number;
  className?: string;
}

export const DualColorProgressBar: React.FC<DualColorProgressBarProps> = ({
  subscriptionRemaining,
  subscriptionMax,
  oneTimeRemaining,
  oneTimeMax,
  className = "",
}) => {
  const { t } = useTranslation();

  // Calculate remaining percentages (quota logic: remaining credits)
  const totalMax = subscriptionMax + oneTimeMax;
  const totalRemaining = subscriptionRemaining + oneTimeRemaining;

  // Calculate widths for progress segments (show remaining quota)
  const subscriptionWidth =
    totalMax > 0 ? (subscriptionRemaining / totalMax) * 100 : 0;
  const oneTimeWidth = totalMax > 0 ? (oneTimeRemaining / totalMax) * 100 : 0;
  const totalRemainingPercentage =
    totalMax > 0 ? Math.round((totalRemaining / totalMax) * 100) : 0;

  const getStatusColor = (percentage: number) => {
    if (percentage <= 10) return "harmony-critical-gradient";
    if (percentage <= 30) return "harmony-warning-gradient";
    return "harmony-accent-gradient";
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Usage Text */}
      <p className="text-sm font-medium text-text-primary dark:text-white">
        {totalRemaining.toLocaleString()}{" "}
        {t("settings.billing.creditsRemaining")} {totalMax.toLocaleString()}{" "}
        {t("settings.billing.creditsTotal")}
        <span className="ml-2 text-xs text-text-tertiary dark:text-gray-400">
          ({totalRemainingPercentage}%)
        </span>
      </p>

      {/* Dual Color Progress Bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden relative">
        {/* Abonelik kalanı — scrollbar ile aynı vurgu gradyanı */}
        {subscriptionWidth > 0 && (
          <div
            className="absolute left-0 top-0 h-full harmony-accent-gradient transition-all duration-300"
            style={{ width: `${subscriptionWidth}%` }}
          />
        )}

        {/* OneTime Remaining (Orange) - positioned after subscription */}
        {oneTimeWidth > 0 && (
          <div
            className="absolute top-0 h-full bg-orange-500 transition-all duration-300"
            style={{
              left: `${subscriptionWidth}%`,
              width: `${oneTimeWidth}%`,
            }}
          />
        )}

        {/* Overall Status Overlay (for critical levels) */}
        {totalRemainingPercentage <= 10 && (
          <div
            className="absolute left-0 top-0 h-full harmony-critical-gradient opacity-90 transition-all duration-300"
            style={{ width: `${Math.min(totalRemainingPercentage, 100)}%` }}
          />
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between text-xs">
        <div className="flex flex-col gap-1 lg:flex-row lg:items-center lg:gap-4">
          {/* Subscription Legend */}
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-module-accent" />
            <span className="text-text-tertiary dark:text-gray-400">
              {t("settings.billing.subscriptionCredits")}
            </span>
            <span className="font-medium text-text-primary dark:text-white">
              {subscriptionRemaining.toLocaleString()}/
              {subscriptionMax.toLocaleString()}
            </span>
          </div>

          {/* OneTime Legend */}
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-orange-500" />
            <span className="text-text-tertiary dark:text-gray-400">
              {t("settings.billing.oneTimeCredits")}
            </span>
            <span className="font-medium text-text-primary dark:text-white">
              {oneTimeRemaining.toLocaleString()}/{oneTimeMax.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Overall Status */}
        <div className="flex items-center gap-1.5 lg:ml-0">
          <div
            className={`w-2 h-2 rounded-full ${getStatusColor(
              totalRemainingPercentage,
            )}`}
          />
          <span className="text-text-tertiary dark:text-gray-400">
            {totalRemainingPercentage <= 10
              ? t("credits.criticalUsage")
              : totalRemainingPercentage <= 30
                ? t("credits.highUsage")
                : t("credits.goodUsage")}
          </span>
        </div>
      </div>
    </div>
  );
};
